import { FlagContext } from '@unleash/proxy-client-react'
import { useContext, useEffect, useState } from 'react'

/**
 * Read an Unleash feature flag reactively.
 *
 * Unlike the library's `useFlag`, this never throws when no `FlagProvider` is
 * mounted — it returns `fallbackWhenNoProvider` instead. Pass the flag's safe
 * default so unit tests, and any render path outside a provider, degrade to the
 * same value an Unleash outage produces rather than crashing.
 */
export const useFeatureFlag = (
  name: string,
  fallbackWhenNoProvider = false
): boolean => {
  const client = useContext(FlagContext)?.client
  const [enabled, setEnabled] = useState(() =>
    client ? client.isEnabled(name) : fallbackWhenNoProvider
  )

  useEffect(() => {
    const sync = () =>
      setEnabled(client ? client.isEnabled(name) : fallbackWhenNoProvider)

    // Resolve the current value once on mount / dependency change, then (when a
    // client exists) keep it in sync with the client's update/ready events.
    sync()

    if (!client) {
      return
    }

    client.on('update', sync)
    client.on('ready', sync)

    return () => {
      client.off('update', sync)
      client.off('ready', sync)
    }
  }, [client, name, fallbackWhenNoProvider])

  return enabled
}
